/**
 * API client for BtGtR backend
 */

// Use the live Cloudflare Worker backend
const API_BASE_URL = 'https://btgtr.digeratidavex.workers.dev/api/v1';

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export interface MagicLinkRequestResponse {
  success: boolean;
  message: string;
  isExistingUser: boolean;
  requiresBirthdate?: boolean;
}

export interface MagicLinkVerifyResponse {
  success: boolean;
  message?: string;
  sessionToken?: string;
  userId?: string;
  contact: string;
  contactType?: 'email' | 'phone';
  needsRegistration?: boolean;
  isEmail?: boolean;
  memberId?: string;
  giftGroupId?: string;
  redirectTo?: string;
  user?: {
    id: string;
    email?: string;
    phone?: string;
    name?: string;
    birthdate?: string;
    creditBalance: number;
  };
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  userId: string;
  sessionToken: string;
  contact: string;
  contactType: 'email' | 'phone';
}

export interface UserProfile {
  id: string;
  email?: string;
  phone?: string;
  name?: string;
  birthdate?: string;
  creditBalance: number;
  adminLevel?: number;
  createdAt: string;
  lastLogin: string;
}

export interface DashboardTodo {
  member_id: string;
  gift_group_id: string;
  gift_group_name: string;
  status: string;
  due_date: string;
  type: string;
  priority: string;
  workflow_state?: string;
}

export interface DashboardCommunication {
  id: string;
  gift_group_id: string;
  type: string;
  message: string;
  read: number;
  created_at: string;
}

export interface DashboardTransaction {
  id: string;
  type: string;
  amount: number;
  description: string;
  created_at: string;
  balance_after: number;
  payment_method?: string;
  payment_provider?: string;
  payment_provider_id?: string;
}

export interface DashboardGiftGroup {
  id: string;
  title: string;
  status: string;
  gift_date: string;
  type_id: string;
  type_name: string;
  role: string;
  member_status: string;
  contributed_amount: number;
  workflow_state?: string;
}

export interface DashboardData {
  todos: DashboardTodo[];
  communications: DashboardCommunication[];
  finances: {
    creditBalance: number;
    allocated: number;
    upcoming90Days: number;
    recentTransactions: DashboardTransaction[];
  };
  giftGroups: DashboardGiftGroup[];
}

class ApiClient {
  private baseUrl: string;
  private sessionToken: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Load session token from localStorage on initialization
    if (typeof window !== 'undefined') {
      this.sessionToken = localStorage.getItem('btgtr_session_token');
    }
  }

  setSessionToken(token: string | null) {
    this.sessionToken = token;
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem('btgtr_session_token', token);
      } else {
        localStorage.removeItem('btgtr_session_token');
      }
    }
  }

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  isAuthenticated(): boolean {
    return !!this.sessionToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {}),
    };

    // Add authorization header if session token exists
    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || 'API request failed');
    }

    return data;
  }

  // Auth endpoints
  async requestMagicLink(
    contact: string,
    context: string = 'dashboard'
  ): Promise<MagicLinkRequestResponse> {
    return this.request<MagicLinkRequestResponse>('/auth/request-magic-link', {
      method: 'POST',
      body: JSON.stringify({ contact, context }),
    });
  }

  async verifyMagicLink(token: string): Promise<MagicLinkVerifyResponse> {
    const response = await this.request<MagicLinkVerifyResponse>('/auth/verify-magic-link', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    
    // Store session token
    if (response.sessionToken) {
      this.setSessionToken(response.sessionToken);
    }
    
    return response;
  }

  async register(contact: string, birthdate: string | null, name: string): Promise<RegisterResponse> {
    const response = await this.request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ contact, birthdate, name }),
    });
    
    // Store session token from registration
    if (response.sessionToken) {
      this.setSessionToken(response.sessionToken);
    }
    
    return response;
  }

  async logout(): Promise<void> {
    this.setSessionToken(null);
  }

  // User endpoints
  async getProfile(): Promise<UserProfile> {
    return this.request<UserProfile>('/users/me');
  }

  async getCurrentUser(): Promise<UserProfile> {
    return this.getProfile();
  }

  async getDashboard(): Promise<DashboardData> {
    return this.request<DashboardData>('/users/me/dashboard');
  }

  async updateProfile(updates: Partial<UserProfile>): Promise<UserProfile> {
    return this.request<UserProfile>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  // Gift Group endpoints
  async createGiftGroup(data: {
    title: string;
    typeId: number;
    giftDate: string;
    orderByDate: string;
    recommendedContribution?: number;
    giftees: string[];
    gifters: string[];
  }): Promise<{
    success: boolean;
    giftGroup: {
      id: string;
      title: string;
      status: string;
      invitationsSent: number;
    };
  }> {
    return this.request('/gift-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGiftGroups(status: string = 'all'): Promise<{
    giftGroups: DashboardGiftGroup[];
  }> {
    const query = status !== 'all' ? `?status=${status}` : '';
    return this.request(`/gift-groups${query}`);
  }

  async getGiftGroup(id: string): Promise<any> {
    return this.request(`/gift-groups/${id}`);
  }

  async getGiftGroupInvitation(memberId: string): Promise<any> {
    return this.request(`/gift-groups/invitations/${memberId}`);
  }

  async getGiftGroupContributionDetails(giftGroupId: string): Promise<any> {
    return this.request(`/gift-groups/${giftGroupId}/contribution-details`);
  }

  async acceptGiftGroupInvitation(memberId: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/invitations/${memberId}/accept`, {
      method: 'POST',
    });
  }

  async declineGiftGroupInvitation(memberId: string, reason?: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/invitations/${memberId}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // Payment endpoints
  async createPayPalOrder(amount: number): Promise<{
    success: boolean;
    orderId: string;
    approvalUrl: string;
    amount: number;
    currency: string;
  }> {
    return this.request('/payments/add-funds/create-order', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  async capturePayPalPayment(orderId: string): Promise<{
    success: boolean;
    amount: number;
    currency: string;
    newBalance: number;
    transactionId: string;
    captureId: string;
  }> {
    return this.request('/payments/add-funds/capture-payment', {
      method: 'POST',
      body: JSON.stringify({ orderId }),
    });
  }

  // Gift Group Contribution
  async contributeToGiftGroup(giftGroupId: string, amount: number): Promise<{
    success: boolean;
    contributedAmount: number;
    newCreditBalance: number;
    memberStatus: string;
    transactionId: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/contribute`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  // Temporary: Add test funds
  async addTestFunds(amount: number): Promise<{ success: boolean; newBalance: number }> {
    return this.request('/users/me/add-test-funds', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  }

  // Gift Suggestions endpoints
  async markAIViewed(groupId: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/${groupId}/mark-ai-viewed`, {
      method: 'POST',
    });
  }

  async markManualViewed(groupId: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/${groupId}/mark-manual-viewed`, {
      method: 'POST',
    });
  }

  async getAISuggestions(groupId: string, descriptors: string[], budget?: number): Promise<{ suggestions: any[] }> {
    return this.request(`/gift-groups/${groupId}/ai-suggestions`, {
      method: 'POST',
      body: JSON.stringify({ descriptors, budget }),
    });
  }

  async addGiftSuggestion(groupId: string, data: {
    url: string;
    name?: string;
    description?: string;
    price?: number;
    imageUrl?: string;
    source?: string;
  }): Promise<{ success: boolean; suggestion: any; needsEditing?: boolean; error?: string }> {
    return this.request(`/gift-groups/${groupId}/suggestions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGiftSuggestions(groupId: string): Promise<{ suggestions: any[] }> {
    return this.request(`/gift-groups/${groupId}/suggestions`);
  }

  async updateGiftSuggestion(groupId: string, suggestionId: string, data: {
    name?: string;
    description?: string;
    price?: number;
    url?: string;
  }): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/${groupId}/suggestions/${suggestionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteGiftSuggestion(groupId: string, suggestionId: string): Promise<{ success: boolean }> {
    return this.request(`/gift-groups/${groupId}/suggestions/${suggestionId}`, {
      method: 'DELETE',
    });
  }

  async updateRankings(groupId: string, rankings: Array<{ suggestionId: number; rank: number }>): Promise<{
    success: boolean;
    updatedGroupRankings: Array<{ suggestionId: number; groupRank: number; averageRank: number; rankedBy: number }>;
  }> {
    return this.request(`/gift-groups/${groupId}/rankings`, {
      method: 'PUT',
      body: JSON.stringify({ rankings }),
    });
  }

  async addComment(groupId: string, content: string, suggestionId?: number, mentions?: number[]): Promise<{
    success: boolean;
    comment: any;
  }> {
    return this.request(`/gift-groups/${groupId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ suggestionId, content, mentions }),
    });
  }

  async getComments(groupId: string, suggestionId?: string): Promise<{ comments: any[] }> {
    const query = suggestionId ? `?suggestionId=${suggestionId}` : '';
    return this.request(`/gift-groups/${groupId}/comments${query}`);
  }

  async getGiftGroupActivities(groupId: string): Promise<{ activities: any[] }> {
    return this.request(`/gift-groups/${groupId}/activities`);
  }

  async addGiftGroupMember(giftGroupId: string, contact: string, role: string = 'gifter'): Promise<{
    success: boolean;
    memberId: string;
    contact: string;
    role: string;
    message: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/add-member`, {
      method: 'POST',
      body: JSON.stringify({ contact, role }),
    });
  }

  async removeGiftGroupMember(giftGroupId: string, memberId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return this.request(`/gift-groups/${giftGroupId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);